import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { GlassCard } from "./GlassCard";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ReminderSettings } from "./ReminderSettings";
import { MoodThemeSettings } from "./MoodAtmosphere";
import { MoodStoryNotifications } from "./MoodStoryNotifications";
import { 
  User, 
  AtSign, 
  Mail, 
  Check, 
  X, 
  Loader2,
  Save,
  Shield,
  Calendar,
  Palette
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProfilePage = () => {
  const { user, authMode, checkUserId, setUserId } = useAuth();
  const [newUserId, setNewUserId] = useState("");
  const [idStatus, setIdStatus] = useState({ checking: false, available: null, error: null });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.user_id) {
      setNewUserId(user.user_id);
    }
  }, [user?.user_id]);

  // Check user ID availability with debounce
  const checkIdAvailability = useCallback(async (id) => {
    // If same as current, it's "available"
    if (id === user?.user_id) {
      setIdStatus({ checking: false, available: true, error: null });
      return;
    }

    if (!id || id.length < 3) {
      setIdStatus({ checking: false, available: null, error: id.length > 0 ? "Mínimo 3 caracteres" : null });
      return;
    }
    
    // Validate format locally first
    if (!/^[a-zA-Z0-9_]+$/.test(id)) {
      setIdStatus({ checking: false, available: false, error: "Solo letras, números y _" });
      return;
    }
    
    if (id.length > 20) {
      setIdStatus({ checking: false, available: false, error: "Máximo 20 caracteres" });
      return;
    }
    
    setIdStatus({ checking: true, available: null, error: null });
    
    const result = await checkUserId(id);
    setIdStatus({ 
      checking: false, 
      available: result.available, 
      error: result.error 
    });
  }, [checkUserId, user?.user_id]);

  // Debounced ID check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (newUserId) {
        checkIdAvailability(newUserId);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [newUserId, checkIdAvailability]);

  const handleSaveId = async () => {
    if (!newUserId || newUserId === user?.user_id) {
      toast.info("No hay cambios para guardar");
      return;
    }

    if (!idStatus.available) {
      toast.error("Por favor elige un ID válido y disponible");
      return;
    }

    setSaving(true);
    try {
      const result = await setUserId(newUserId);
      toast.success(result.message || "¡ID actualizado correctamente!");
    } catch (error) {
      const msg = error.response?.data?.detail || "Error al actualizar ID";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const IdStatusIndicator = () => {
    if (!newUserId || newUserId === user?.user_id) return <Check className="w-4 h-4 text-neon-green" />;
    
    if (idStatus.checking) {
      return <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />;
    }
    if (idStatus.available === true) {
      return <Check className="w-4 h-4 text-neon-green" />;
    }
    if (idStatus.available === false || idStatus.error) {
      return <X className="w-4 h-4 text-red-400" />;
    }
    return null;
  };

  if (authMode === "incognito") {
    return (
      <div className="max-w-2xl mx-auto">
        <GlassCard className="p-8 text-center">
          <Shield className="w-16 h-16 text-neon-purple mx-auto mb-4" />
          <h2 className="font-orbitron text-2xl text-white mb-2">Modo Incógnito</h2>
          <p className="text-slate-400 font-inter mb-6">
            Estás usando MoodPunk en modo incógnito. Tus datos solo se guardan en este dispositivo.
          </p>
          <p className="text-sm text-slate-500">
            Para tener un perfil personalizado y acceder desde cualquier dispositivo, 
            <span className="text-neon-cyan"> crea una cuenta MoodPunk</span>.
          </p>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Profile Header */}
      <GlassCard className="p-6">
        <div className="flex items-center gap-4 mb-6">
          {user?.picture ? (
            <img 
              src={user.picture} 
              alt={user.name} 
              className="w-20 h-20 rounded-full border-2 border-neon-cyan/50"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-neon-purple/20 border-2 border-neon-purple/50 flex items-center justify-center">
              <User className="w-10 h-10 text-neon-purple" />
            </div>
          )}
          <div>
            <h1 className="font-orbitron text-2xl text-white">{user?.name}</h1>
            <p className="text-slate-400 font-rajdhani flex items-center gap-1">
              <AtSign className="w-4 h-4" />
              {user?.user_id}
            </p>
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
              <Mail className="w-3 h-3" />
              {user?.email}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Calendar className="w-4 h-4" />
          <span>Cuenta {authMode === "google" ? "Google" : "MoodPunk"}</span>
        </div>
      </GlassCard>

      {/* Change User ID */}
      <GlassCard className="p-6">
        <h2 className="font-rajdhani font-bold text-lg text-white uppercase tracking-wide mb-4 flex items-center gap-2">
          <AtSign className="w-5 h-5 text-neon-cyan" />
          Tu ID Único
        </h2>
        
        <p className="text-sm text-slate-400 mb-4">
          Tu ID es como te encuentran otros usuarios en el chat. Puedes cambiarlo cuando quieras.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-xs text-slate-400 font-rajdhani mb-1 ml-1">
              Nuevo ID (letras, números y _ solamente)
            </label>
            <div className="relative">
              <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                type="text"
                placeholder="mi_nuevo_id"
                value={newUserId}
                onChange={(e) => setNewUserId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                data-testid="profile-userid-input"
                className={`pl-11 pr-10 bg-black/50 border-white/10 text-white ${
                  newUserId === user?.user_id ? "border-neon-green/30" :
                  idStatus.available === true ? "border-neon-green/50 focus:border-neon-green/50" :
                  idStatus.available === false ? "border-red-500/50 focus:border-red-500/50" :
                  "focus:border-neon-cyan/50"
                }`}
                maxLength={20}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <IdStatusIndicator />
              </div>
            </div>
            {idStatus.error && (
              <p className="text-xs text-red-400 mt-1 ml-1">{idStatus.error}</p>
            )}
            {idStatus.available && newUserId !== user?.user_id && (
              <p className="text-xs text-neon-green mt-1 ml-1">¡ID disponible!</p>
            )}
            {newUserId === user?.user_id && (
              <p className="text-xs text-slate-500 mt-1 ml-1">Este es tu ID actual</p>
            )}
          </div>

          <Button
            onClick={handleSaveId}
            disabled={saving || !idStatus.available || newUserId === user?.user_id}
            data-testid="save-userid-btn"
            className="w-full py-4 font-rajdhani font-bold uppercase tracking-widest bg-neon-cyan/20 hover:bg-neon-cyan/30 text-neon-cyan border border-neon-cyan/50 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </div>
      </GlassCard>

      {/* Account Info */}
      <GlassCard className="p-6">
        <h2 className="font-rajdhani font-bold text-lg text-white uppercase tracking-wide mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-neon-purple" />
          Información de Cuenta
        </h2>

        <div className="space-y-3 text-sm">
          <div className="flex justify-between items-center py-2 border-b border-white/5">
            <span className="text-slate-400">Tipo de cuenta</span>
            <span className="text-white font-rajdhani">
              {authMode === "google" ? "Google" : "MoodPunk"}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-white/5">
            <span className="text-slate-400">Email</span>
            <span className="text-white font-rajdhani">{user?.email}</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-slate-400">ID de usuario</span>
            <span className="text-neon-cyan font-rajdhani">@{user?.user_id}</span>
          </div>
        </div>
      </GlassCard>

      {/* Mood Theme Settings */}
      <GlassCard className="p-6">
        <h2 className="font-rajdhani font-bold text-lg text-white uppercase tracking-wide mb-4 flex items-center gap-2">
          <Palette className="w-5 h-5 text-neon-purple" />
          Tema Visual
        </h2>
        <MoodThemeSettings />
      </GlassCard>

      {/* Mood Story Notifications */}
      <MoodStoryNotifications />

      {/* Reminder Settings */}
      <ReminderSettings />
    </div>
  );
};
